import { BeaconRoom } from 'hooks/useSpaces'

// --------------------
// Shared Types
// --------------------

export type MIOptions = {
  mapId: string
  key: string
  secret: string
}

export type MappedinFloor = {
  id: string
  name: string
  [key: string]: any
}

export type MappedinSpace = {
  id: string
  name: string
  floorId: string
  externalId?: string
  polygon: {
    coordinates: number[][][] // GeoJSON-style: [[[lng, lat], [lng, lat], ...]]
  }
}

export type POI = {
  id: string
  name: string
  floorId: string
  spaceId: string | null
  coordinates: {
    lat: number
    long: number
    floorId: string
  }
}

export type POIMatch = {
  poiId: string
  poiName: string
  coordinates: POI['coordinates']
  beaconId: string
  roomName: string
}

export type POIMatchMap = Record<string, POIMatch>

export type FloorMap = Record<string, string>
export type SpaceMap = Record<string, MappedinSpace[]>
export type RoomMap = Record<string, BeaconRoom[]>

export type SpaceMatchResult = {
  byFloor: {
    [floorId: string]: {
      matched: { mi: MappedinSpace; rm: string }[]
      unmatchedMi: MappedinSpace[]
      unmatchedRm: string[]
    }
  }
  matchedSpaces: {
    [floorId: string]: {
      [spaceId: string]: string
    }
  }
}

// --------------------
// Floor & Space Matching
// --------------------

export function compareFloors(
  mappedinFloors: MappedinFloor[],
  reactMobileFloors: string[]
) {
  const normalizedMi = mappedinFloors.map(f => ({
    ...f,
    _normalizedName: f.name.trim().toLowerCase(),
  }))
  const normalizedRm = reactMobileFloors.map(f => f.trim().toLowerCase())

  const inBoth: { mi: MappedinFloor; rm: string }[] = []
  const inMappedinOnly: MappedinFloor[] = []
  const inReactOnly: string[] = []

  for (const miFloor of normalizedMi) {
    if (normalizedRm.includes(miFloor._normalizedName)) {
      inBoth.push({
        mi: miFloor,
        rm: normalizedRm.find(r => r === miFloor._normalizedName)!,
      })
    } else {
      inMappedinOnly.push(miFloor)
    }
  }

  for (const rmName of normalizedRm) {
    const matched = normalizedMi.find(mi => mi._normalizedName === rmName)
    if (!matched) {
      inReactOnly.push(rmName)
    }
  }

  return {
    inBoth,
    inMappedinOnly,
    inReactOnly,
  }
}

export function compareSpaces(
  miSpacesByFloor: Record<string, MappedinSpace[]>,
  rmRoomsByFloor: RoomMap
): SpaceMatchResult {
  const byFloor: SpaceMatchResult['byFloor'] = {}
  const matchedByFloorId: SpaceMatchResult['matchedSpaces'] = {}

  Object.entries(miSpacesByFloor).forEach(([floorId, miSpaces]) => {
    const rmRooms = rmRoomsByFloor[floorId] || []
    const usedRmRoomNames = new Set<string>()

    const matched: { mi: MappedinSpace; rm: string }[] = []
    const unmatchedMi: MappedinSpace[] = []
    const matchedMap: Record<string, string> = {}

    miSpaces.forEach(mi => {
      const match = rmRooms.find(rm => {
        const rmName = rm.room.toLowerCase()
        return rmName === mi.name.toLowerCase() && !usedRmRoomNames.has(rmName)
      })
      if (match) {
        matched.push({ mi, rm: match.room })
        matchedMap[mi.id] = match.room
        usedRmRoomNames.add(match.room.toLowerCase())
      } else {
        unmatchedMi.push(mi)
      }
    })

    const unmatchedRm: string[] = rmRooms
      .map(r => r.room)
      .filter(r => !usedRmRoomNames.has(r.toLowerCase()))

    byFloor[floorId] = { matched, unmatchedMi, unmatchedRm }
    matchedByFloorId[floorId] = matchedMap
  })

  return {
    byFloor,
    matchedSpaces: matchedByFloorId,
  }
}

// --------------------
// POI to Beacon Matching (Raycasting)
// --------------------

function pointInPolygon(lng: number, lat: number, polygon: number[][]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]

    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi

    if (intersect) inside = !inside
  }
  return inside
}

function findSpaceByRaycast(
  lat: number,
  lng: number,
  spaces: MappedinSpace[]
): MappedinSpace | null {
  for (const space of spaces) {
    const raw = space.polygon?.coordinates?.[0]
    if (!Array.isArray(raw)) {
      console.warn(`~~ray ❌ SKIPPED space '${space.name}' — no usable polygon`)
      continue
    }

    console.log(`~~ray: testing space '${space.name}' [${raw.length} pts]`)
    const inside = pointInPolygon(lng, lat, raw)
    if (inside) return space
  }
  return null
}

export function matchPOIsToBeaconsFromMaps(
  pois: POI[],
  matchedFloors: Record<string, string>,
  matchedSpaces: Record<string, Record<string, string>>,
  spaceMap: SpaceMap,
  roomMap: RoomMap
): POIMatchMap {
  console.log('~~ray: MATCH START')
  console.log('~~ray: matchedFloors', matchedFloors)
  console.log('~~ray: spaceMap keys', Object.keys(spaceMap))
  console.log('~~ray: roomMap keys', Object.keys(roomMap))
  console.log('~~ray: POIs', pois.map(p => `${p.name || p.id} @ ${p.coordinates.lat},${p.coordinates.long}`))

  const result: POIMatchMap = {}

  pois.forEach(poi => {
    const floorId = poi.floorId
    const floorName = matchedFloors[floorId]
    const lat = poi.coordinates.lat
    const lng = poi.coordinates.long

    if (!lat || !lng || !floorName) {
      console.warn(`❌ ~~ray POI '${poi.name || poi.id}' is missing coordinates or floorName.`)
      return
    }

    console.log(`~~ray: checking POI '${poi.name || poi.id}' on floor '${floorId}' (→ '${floorName}') at [${lat}, ${lng}]`)

    const spaces = spaceMap[floorId] || spaceMap[floorName]
    console.log('~~ray-spaces', spaces)
    if (!spaces?.length) {
      console.warn(`❌ ~~ray No spaces found on floor '${floorId}' or name '${floorName}' for POI '${poi.name || poi.id}'`)
      return
    }

    const resolvedSpace = findSpaceByRaycast(lat, lng, spaces)
    if (!resolvedSpace) {
      console.warn(`❌ ~~ray Raycast failed — no containing space for POI '${poi.name || poi.id}'`)
      return
    }

    console.log(`✅ ~~ray POI '${poi.name || poi.id}' is inside space '${resolvedSpace.name}' (id: ${resolvedSpace.id})`)

    const spaceId = resolvedSpace.id
    const roomName = matchedSpaces[floorId]?.[spaceId] || matchedSpaces[floorName]?.[spaceId]

    console.log(`~~ray: resolved roomName='${roomName}', floorName='${floorName}'`)

    if (!roomName || !floorName) {
      console.warn(`❌ ~~ray No matched room for POI '${poi.name || poi.id}' (spaceId: ${spaceId})`)
      return
    }

    const beacons: BeaconRoom[] = roomMap[floorName]?.filter(b => b.room === roomName) || []
    console.log(`~~ray: found ${beacons.length} beacon(s) in room '${roomName}'`)

    if (beacons.length === 1) {
      console.log(`✅ ~~ray Direct match: '${poi.name || poi.id}' → beacon '${beacons[0].name}' in room '${roomName}'`)
      result[poi.id] = {
        poiId: poi.id,
        poiName: poi.name,
        coordinates: poi.coordinates,
        beaconId: beacons[0].id,
        roomName: roomName
      }
    } else if (beacons.length > 1) {
      const match = beacons.find(b => {
        const query = poi.name?.toLowerCase() || ''
        return b.name.toLowerCase().includes(query)
      })
      if (match) {
        console.log(`✅ ~~ray Matched POI '${poi.name || poi.id}' to beacon '${match.name}' by name in room '${roomName}'`)
        result[poi.id] = {
          poiId: poi.id,
          poiName: poi.name,
          coordinates: poi.coordinates,
          beaconId: match.id,
          roomName: roomName
        }
      } else {
        console.warn(`❌ ~~ray Ambiguous match for POI '${poi.name || poi.id}' — multiple beacons but no name match`)
      }
    } else {
      console.warn(`❌ ~~ray No beacons found for room '${roomName}'`)
    }
  })

  return result
}